import React, { useRef, useCallback } from 'react';

export type TabType = 'performance' | 'key-moments' | 'case-recording';
export type PerformanceFilterType = 'TEAM' | 'USER';

interface TabNavigationProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  performanceFilter: PerformanceFilterType;
  onPerformanceFilterChange: (filter: PerformanceFilterType) => void;
  disabledTabs?: TabType[];
}

/**
 * TODO: Re-enable when performance filter feature is ready
 * const PERFORMANCE_FILTER_OPTIONS: { value: PerformanceFilterType; label: string }[] = [
 *   { value: 'TEAM', label: "Surgical Team's Performance" },
 *   { value: 'USER', label: 'My Performance' },
 * ];
 */
const PERFORMANCE_FILTER_OPTIONS: { value: PerformanceFilterType; label: string }[] = [];

export const TABS: { id: TabType; label: string }[] = [
  { id: 'performance', label: 'Performance' },
  // { id: 'key-moments', label: 'Key Moments' }, TODO: Re-enable when we have key moment data.
  { id: 'case-recording', label: 'Case Recording' },
];

export function TabNavigation({
  activeTab,
  onTabChange,
  performanceFilter,
  onPerformanceFilterChange,
  disabledTabs = []
}: TabNavigationProps): React.JSX.Element {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const isTabDisabled = useCallback(
    (tabId: TabType): boolean => disabledTabs.includes(tabId),
    [disabledTabs]
  );

  const findNextEnabledTab = useCallback(
    (startIndex: number, direction: 1 | -1): number | null => {
      for (let i = 1; i <= TABS.length; i++) {
        const nextIndex = (startIndex + i * direction + TABS.length) % TABS.length;
        if (!isTabDisabled(TABS[nextIndex].id)) {
          return nextIndex;
        }
      }
      return null;
    },
    [isTabDisabled]
  );

  const handleTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      let newIndex: number | null = null;

      if (event.key === 'ArrowRight') {
        newIndex = findNextEnabledTab(currentIndex, 1);
      } else if (event.key === 'ArrowLeft') {
        newIndex = findNextEnabledTab(currentIndex, -1);
      } else if (event.key === 'Home') {
        // Find first enabled tab
        newIndex = TABS.findIndex(tab => !isTabDisabled(tab.id));
        if (newIndex === -1) newIndex = null;
      } else if (event.key === 'End') {
        // Find last enabled tab
        for (let i = TABS.length - 1; i >= 0; i--) {
          if (!isTabDisabled(TABS[i].id)) {
            newIndex = i;
            break;
          }
        }
      }

      if (newIndex !== null) {
        event.preventDefault();
        tabRefs.current[newIndex]?.focus();
        onTabChange(TABS[newIndex].id);
      }
    },
    [onTabChange, findNextEnabledTab, isTabDisabled]
  );

  const showPerformanceFilter = activeTab === 'performance' && PERFORMANCE_FILTER_OPTIONS.length > 0;

  return (
    <div className={`flex flex-col ${showPerformanceFilter ? 'min-[744px]:flex-row min-[744px]:items-end min-[744px]:gap-4' : ''} gap-4`}>
      {/* Tabs container with underline */}
      <div className={`relative h-11 ${showPerformanceFilter ? 'flex-1' : 'w-full'}`}>
        <div
          role="tablist"
          aria-label="Case report sections"
          className="flex items-end gap-6 h-full"
        >
          {TABS.map((tab, index) => {
            const isSelected = activeTab === tab.id;
            const isDisabled = isTabDisabled(tab.id);
            return (
              <button
                key={tab.id}
                id={`tab-${tab.id}`}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-controls={`tabpanel-${tab.id}`}
                aria-disabled={isDisabled}
                tabIndex={isSelected ? 0 : -1}
                disabled={isDisabled}
                onClick={() => !isDisabled && onTabChange(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, index)}
                className={`relative pb-2 transition-colors z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${isDisabled ? 'cursor-not-allowed !bg-transparent' : ''}`}
              >
                <span
                  className={
                    isDisabled
                      ? 'text-low-emphasis-font'
                      : isSelected
                        ? 'font-semibold text-primary'
                        : 'text-high-emphasis-font'
                  }
                >
                  {tab.label}
                </span>
                {isSelected && !isDisabled && (
                  <div className="absolute bottom-[2px] left-0 right-0 h-[3px] bg-primary" />
                )}
              </button>
            );
          })}
        </div>

        {/* Underline - only under tabs */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-surface-400" />
      </div>

      {showPerformanceFilter && (
        <div className="w-full min-[744px]:w-[320px] shrink-0">
          <select
            value={performanceFilter}
            onChange={(e) => onPerformanceFilterChange(e.target.value as PerformanceFilterType)}
            aria-label="Performance filter"
            className="w-full h-10 px-3 rounded-lg border border-border bg-white text-high-emphasis-font focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            {PERFORMANCE_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
